import { useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Button } from 'antd';
import { trackName, withArticle } from './corrections';

/**
 * "Your changes" in the count panel (design: corrections_design.html, sections 2–3): what the edits did in one line
 * of pills, then each edit as a small card, newest first — what it was, how and when, and Undo. An added object that
 * could not be followed is amber with Retry and Remove. Run again with what was taught sits under the list.
 */

const tk = ({ theme: t }) => t.token;

const Box = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${p => tk(p).marginXS}px;
`;

const Head = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: baseline;
	gap: ${p => tk(p).marginXS}px;
`;

const Label = styled.span`
	font-size: ${p => tk(p).fontSizeXXS}px;
	font-weight: ${p => tk(p).fontWeightStrong};
	letter-spacing: 0.09em;
	text-transform: uppercase;
	color: ${p => tk(p).colorTextTertiary};
`;

const Small = styled.span`
	font-size: ${p => tk(p).fontSizeXS}px;
	color: ${p => tk(p).colorTextTertiary};
`;

const Pills = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: ${p => tk(p).marginXXS}px;
	font-size: ${p => tk(p).fontSizeXS}px;
	color: ${p => tk(p).colorTextSecondary};
`;

const PILL = {
	add: p => css`
		color: ${tk(p).colorBrandLight};
		border-color: ${tk(p).colorBrandLight};
	`,
	remove: p => css`
		color: ${tk(p).colorErrorHover};
		border-color: ${tk(p).colorErrorBorderHover};
	`,
	merge: p => css`
		color: ${tk(p).colorTextSecondary};
		border-color: ${tk(p).colorBorder};
	`,
};

const Pill = styled.span`
	padding: ${p => `${tk(p).lineWidthBold}px ${tk(p).paddingXS}px`};
	border-radius: ${p => tk(p).borderRadiusLG * 4}px;
	border: ${p => `${tk(p).lineWidth}px ${tk(p).lineType}`};
	font-weight: ${p => tk(p).fontWeightStrong};
	${p => PILL[p.$kind]?.(p)}
`;

const breathe = keyframes`
	0%, 100% { opacity: 0.35; }
	50% { opacity: 1; }
`;

const Card = styled.div`
	display: grid;
	grid-template-columns: auto 1fr auto;
	align-items: center;
	gap: ${p => tk(p).marginXS}px;
	padding: ${p => `${tk(p).paddingXXS}px ${tk(p).paddingXS}px`};
	border-radius: ${p => tk(p).borderRadius}px;
	border: ${p =>
		`${tk(p).lineWidth}px ${p.$pending ? 'dashed' : tk(p).lineType} ${
			p.$amber ? tk(p).colorWarningBorderHover : tk(p).colorBorder
		}`};
	background: ${p => tk(p).colorFillQuaternary};
	font-size: ${p => tk(p).fontSizeXS}px;
	color: ${p => tk(p).colorTextSecondary};
`;

const ICON = {
	add: p => css`
		background: ${tk(p).colorBrandDark};
		color: ${tk(p).colorTextLightSolid};
	`,
	remove: p => css`
		background: ${tk(p).colorErrorBg};
		color: ${tk(p).colorErrorHover};
	`,
	merge: p => css`
		background: ${tk(p).colorFillSecondary};
		color: ${tk(p).colorTextLightSolid};
	`,
	amber: p => css`
		background: ${tk(p).colorWarningBg};
		color: ${tk(p).colorWarning};
	`,
};

const Icon = styled.i`
	display: grid;
	place-items: center;
	width: ${p => tk(p).controlHeightXS + tk(p).lineWidthBold * 2}px;
	height: ${p => tk(p).controlHeightXS + tk(p).lineWidthBold * 2}px;
	border-radius: 50%;
	font-style: normal;
	font-weight: ${p => tk(p).fontWeightStrong};
	${p => ICON[p.$kind]?.(p)}
	${p =>
		p.$pending &&
		css`
			animation: ${breathe} 1.2s ease-in-out infinite;
		`}
`;

const Text = styled.span`
	min-width: 0;
	b {
		display: block;
		color: ${p => tk(p).colorTextLightSolid};
		font-weight: ${p => tk(p).fontWeightStrong};
	}
	span {
		display: block;
		color: ${p => tk(p).colorTextTertiary};
		font-size: ${p => tk(p).fontSizeXXS}px;
	}
`;

const Acts = styled.span`
	display: inline-flex;
	gap: ${p => tk(p).marginXS}px;
`;

const Act = styled.button`
	border: 0;
	padding: 0;
	background: transparent;
	color: ${p => tk(p).colorBrandLight};
	font-size: ${p => tk(p).fontSizeXS}px;
	text-decoration: underline;
	cursor: pointer;
	&:disabled {
		opacity: 0.4;
		cursor: default;
	}
`;

const TOOL = {
	click: 'click',
	box: 'box',
	assistant_hint: 'the assistant',
	manual: 'box',
	dot: 'you',
};
const SHOWN = ['ready', 'following', 'failed', 'unmatched'];
const FIRST = 5;

// follow: the job following added objects ({ status: 'starting' | 'following' }), while one runs
const ChangesList = ({
	changes,
	edits,
	subject,
	fps = 30,
	follow = null,
	onUndo,
	onRetry,
	onRunAgain,
}) => {
	const [all, setAll] = useState(false);
	const [starting, setStarting] = useState(false);
	// an added object only becomes an example once it has been followed, so Run again waits for them
	const following = (edits || []).some(e => e.type === 'add' && e.status === 'following');
	const runAgain = async () => {
		setStarting(true);
		try {
			await onRunAgain();
		} finally {
			setStarting(false);
		}
	};
	const shown = (edits || [])
		.filter(e => SHOWN.includes(e.status))
		.slice()
		.reverse();
	if (!shown.length) return null;
	const at = f => {
		const sec = Math.round((f || 0) / (fps || 30));
		return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
	};
	const card = e => {
		const tool = TOOL[e.tool] || 'you';
		if (e.status === 'unmatched')
			return { kind: 'amber', title: `${titleOf(e)}`, sub: 'not found in this version' };
		if (e.type === 'add') {
			if (e.status === 'following')
				return {
					kind: 'add',
					pending: true,
					title: `Added · ${e.label || subject} at ${at(e.frame)}`,
					sub:
						follow?.status === 'starting'
							? `${tool} · a machine is starting to follow it (about 2 min)`
							: `${tool} · being followed through the video…`,
				};
			if (e.status === 'failed')
				return {
					kind: 'amber',
					title: `Added · ${e.label || subject} at ${at(e.frame)}`,
					sub: 'could not follow — counts on this frame only',
					failed: true,
				};
			const t = e.track?.t;
			return {
				kind: 'add',
				title: `Added · ${e.label || subject} at ${at(e.frame)}`,
				sub: t?.length ? `${tool} · followed ${at(t[0])}–${at(t[t.length - 1])}` : tool,
			};
		}
		if (e.type === 'merge') return { kind: 'merge', title: titleOf(e), sub: 'the same object' };
		return {
			kind: 'remove',
			title: titleOf(e),
			sub:
				e.type === 'negative'
					? 'and everything like it, when run again'
					: e.reason
						? `the assistant: ${e.reason}`
						: 'removed from every frame',
		};
	};
	function titleOf(e) {
		if (e.type === 'merge') return `Merged · ${(e.tracks || []).map(trackName).join(' + ')}`;
		if (e.type === 'negative') return `Not ${withArticle(subject)} · ${trackName(e.tracks?.[0])}`;
		if (e.type === 'add') return `Added · ${e.label || subject} at ${at(e.frame)}`;
		return `Removed · ${trackName(e.tracks?.[0])}${e.frame != null ? ` at ${at(e.frame)}` : ''}`;
	}
	const icon = { add: '+', remove: '−', merge: '⇄', amber: '!' };
	return (
		<Box>
			<Head>
				<Label>Your changes</Label>
				<Small>
					{shown.length} edit{shown.length === 1 ? '' : 's'} · this version
				</Small>
			</Head>
			{changes && (
				<Pills>
					{changes.added > 0 && <Pill $kind="add">+{changes.added} added</Pill>}
					{changes.removed > 0 && <Pill $kind="remove">−{changes.removed} removed</Pill>}
					{changes.merged > 0 && <Pill $kind="merge">⇄ {changes.merged} merged</Pill>}
					<span>by you</span>
				</Pills>
			)}
			{(all ? shown : shown.slice(0, FIRST)).map(e => {
				const c = card(e);
				return (
					<Card key={e.id} $pending={c.pending} $amber={c.kind === 'amber'}>
						<Icon $kind={c.kind} $pending={c.pending}>
							{icon[c.kind]}
						</Icon>
						<Text>
							<b>{c.title}</b>
							<span>{c.sub}</span>
						</Text>
						<Acts>
							{c.failed && onRetry && (
								<Act type="button" onClick={() => onRetry(e.id)}>
									Retry
								</Act>
							)}
							<Act type="button" disabled={e.pending} onClick={() => onUndo?.(e.id)}>
								{c.failed ? 'Remove' : 'Undo'}
							</Act>
						</Acts>
					</Card>
				);
			})}
			{shown.length > FIRST && (
				<Act type="button" onClick={() => setAll(v => !v)}>
					{all ? 'Show fewer' : `Show all ${shown.length}`}
				</Act>
			)}
			{onRunAgain && (
				<Button block disabled={following} loading={starting} onClick={runAgain}>
					Run again with what I taught it
				</Button>
			)}
			{onRunAgain && following && <Small>It can run again once your added objects are followed.</Small>}
		</Box>
	);
};

export default ChangesList;
